import React from 'react';
import { Button } from '@/components/ui/Button';
import { Check, X, RotateCcw, HelpCircle, UserPlus } from 'lucide-react';
import { usePermission } from '@/hooks/usePermission';

interface ApprovalActionBarProps {
  currentStageId?: string;
  isActionable: boolean;
  onOpenApproveModal: () => void;
  onOpenRejectModal: () => void;
  onOpenSendBackModal: () => void;
  onOpenClarificationModal: () => void;
  onOpenDelegateModal: () => void;
  isProcessing?: boolean;
}

export const ApprovalActionBar: React.FC<ApprovalActionBarProps> = ({
  isActionable,
  onOpenApproveModal,
  onOpenRejectModal,
  onOpenSendBackModal,
  onOpenClarificationModal,
  onOpenDelegateModal,
  isProcessing = false,
}) => {
  const { can, isAuditorReadOnly } = usePermission();
  const canApprove = (can('REQUEST_APPROVE') || can('NFA_APPROVE')) && !isAuditorReadOnly();

  if (!isActionable || !canApprove) {
    return (
      <div className="rounded-xl border border-border bg-muted/30 p-4 text-center text-xs text-foreground-secondary font-medium">
        🔒 Workflow Stage Inactive — Approval actions locked or reserved for assigned stage approver.
      </div>
    );
  }

  return (
    <div className="sticky bottom-4 z-20 rounded-xl border border-border bg-card/95 p-4 shadow-xl backdrop-blur-md flex flex-wrap items-center justify-between gap-3 animate-in slide-in-from-bottom-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold uppercase tracking-wider text-foreground">
          Pending Approval Decision
        </span>
        <span className="text-[10px] text-foreground-muted hidden sm:inline">
          • Action will be recorded in audit log with version timestamp
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Additional: Delegate */}
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs font-semibold"
          disabled={isProcessing}
          onClick={onOpenDelegateModal}
        >
          <UserPlus className="h-3.5 w-3.5 mr-1 text-indigo-500" /> Delegate
        </Button>

        {/* Secondary: Request Clarification */}
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs font-semibold"
          disabled={isProcessing}
          onClick={onOpenClarificationModal}
        >
          <HelpCircle className="h-3.5 w-3.5 mr-1 text-amber-500" /> Clarification
        </Button>

        {/* Secondary: Send Back */}
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs font-semibold text-amber-600 hover:bg-amber-50 border-amber-200"
          disabled={isProcessing}
          onClick={onOpenSendBackModal}
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Send Back
        </Button>

        {/* Destructive: Reject */}
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs font-semibold text-rose-600 hover:bg-rose-50 border-rose-200"
          disabled={isProcessing}
          onClick={onOpenRejectModal}
        >
          <X className="h-3.5 w-3.5 mr-1" /> Reject
        </Button>

        {/* Primary: Approve */}
        <Button
          size="sm"
          className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 font-bold px-4 shadow-sm"
          isLoading={isProcessing}
          onClick={onOpenApproveModal}
        >
          <Check className="h-3.5 w-3.5 mr-1" /> Approve NFA
        </Button>
      </div>
    </div>
  );
};
